import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Code2, Wrench, Plug, Shield, CheckCircle } from "lucide-react";
import { usePageSeo, BASE_URL } from "@/hooks/useSeo";

export default function SoftwareEntwicklungPage() {
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  usePageSeo({
    title: "Software Entwicklung | LAB336 – Massgeschneiderte Lösungen",
    description: "Individuelle Softwareentwicklung für Schweizer KMU. Von der Konzeption bis zum Go-Live entwickeln wir massgeschneiderte digitale Produkte mit modernen Technologien.",
    canonical: `${BASE_URL}/software-entwicklung`,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          "name": "Software Entwicklung",
          "description": "Massgeschneiderte Softwarelösungen für Schweizer KMU",
          "provider": { "@type": "Organization", "name": "LAB336", "url": BASE_URL },
          "areaServed": { "@type": "Country", "name": "Schweiz" },
          "serviceType": "Softwareentwicklung"
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": BASE_URL },
            { "@type": "ListItem", "position": 2, "name": "Leistungen", "item": `${BASE_URL}/#services` },
            { "@type": "ListItem", "position": 3, "name": "Software Entwicklung", "item": `${BASE_URL}/software-entwicklung` }
          ]
        }
      ]
    }
  });

  const goToScheduler = () => {
    navigate("/");
    setTimeout(() => {
      const el = document.querySelector("#scheduler");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 400);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="service-page-software-entwicklung">
      <header className="bg-lab-primary text-white py-4 px-6 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-playfair text-xl tracking-wide">LAB<span className="text-lab-accent">336</span></Link>
          <Link to="/" className="text-sm text-white/80 hover:text-white flex items-center gap-1 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Startseite
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-lab-bg-light py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="w-16 h-16 bg-white flex items-center justify-center mb-6">
              <Code2 className="w-8 h-8 text-lab-accent" strokeWidth={1.5} />
            </div>
            <span className="text-lab-accent text-sm font-semibold tracking-widest uppercase">Leistung</span>
            <h1 className="font-playfair text-3xl lg:text-5xl font-semibold text-lab-primary mt-4 max-w-3xl">
              Software, die genau das tut, was Sie brauchen
            </h1>
            <p className="text-lab-text-muted text-lg mt-6 max-w-2xl leading-relaxed">
              Standardsoftware passt nie ganz. Wir entwickeln individuelle Lösungen, die sich Ihrem Unternehmen anpassen — nicht umgekehrt.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Nutzenargumentation */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="font-playfair text-2xl lg:text-3xl font-semibold text-lab-primary mb-6">Warum Individualsoftware sich lohnt</h2>
            <div className="text-lab-text-muted leading-relaxed space-y-4">
              <p>
                Viele KMU kämpfen mit dem gleichen Problem: Sie haben eine Standardsoftware im Einsatz, die 80% ihrer Anforderungen abdeckt — und für die restlichen 20% haben sie Workarounds gebaut. Excel-Tabellen als Ergänzung zum CRM. Copy-Paste zwischen Systemen. Manuelle Schritte, die eigentlich automatisch laufen sollten. Diese 20% kosten unverhältnismässig viel Zeit und Nerven.
              </p>
              <p>
                Individuelle Software schliesst genau diese Lücke. Sie ersetzt nicht unbedingt Ihre bestehenden Systeme, sondern ergänzt sie dort, wo es am meisten bringt: mit Schnittstellen, die Daten automatisch synchronisieren, mit Dashboards, die Ihnen genau die Kennzahlen zeigen, die Sie brauchen, und mit Workflows, die Ihre tatsächlichen Abläufe abbilden — nicht ein theoretisches Ideal.
              </p>
              <p>
                Der Vorteil gegenüber grossen Softwarehäusern: Bei uns sprechen Sie direkt mit den Entwicklern, nicht mit Verkäufern. Wir verstehen Ihre Anforderungen und setzen sie ohne Umwege um. Jede Woche sehen Sie den Fortschritt, können Feedback geben und die Richtung anpassen. So entsteht keine Software, die am Bedarf vorbei entwickelt wird.
              </p>
              <p>
                Und weil wir als Schweizer Unternehmen arbeiten, wissen wir, wie KMU hier ticken: pragmatisch, kostenbewusst und mit dem Anspruch, dass Dinge einfach funktionieren. Genau so entwickeln wir Software.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="bg-lab-bg-light py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="font-playfair text-2xl lg:text-3xl font-semibold text-lab-primary mb-4">Beispiele aus unserer Arbeit</h2>
            <p className="text-lab-text-muted mb-10">Individuelle Software für konkrete Geschäftsprobleme:</p>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  title: "Personalvermittlung mit 10 Mitarbeitenden",
                  problem: "Kandidatenprofile wurden in Word-Dokumenten gepflegt. Matching mit offenen Stellen war reine Kopfarbeit.",
                  solution: "Eigenes Bewerbermanagement-System mit KI-gestütztem Skill-Matching und Kundenportal.",
                  result: "Vermittlungszeit von 3 Wochen auf 5 Tage reduziert."
                },
                {
                  title: "Logistikunternehmen mit 35 Mitarbeitenden",
                  problem: "Routenplanung und Auftragsverteilung liefen manuell per Telefon und WhatsApp.",
                  solution: "Web-App für Echtzeit-Disposition mit GPS-Tracking und automatischer Routenoptimierung.",
                  result: "20% weniger Fahrkilometer, Disposition spart 3 Stunden täglich."
                },
                {
                  title: "Beratungsfirma mit 8 Mitarbeitenden",
                  problem: "Kundenportal, Zeiterfassung und Rechnungsstellung waren drei separate Systeme ohne Verbindung.",
                  solution: "Integrierte Plattform: Kundenportal mit Zeiterfassung, automatischer Rechnungsstellung und Reporting.",
                  result: "Monatlicher Buchhaltungsaufwand von 2 Tagen auf 3 Stunden."
                },
              ].map((item) => (
                <div key={item.title} className="bg-white border border-lab-border p-6">
                  <h3 className="font-playfair text-lg font-semibold text-lab-primary mb-3">{item.title}</h3>
                  <div className="space-y-2 text-sm">
                    <p className="text-lab-text-muted"><span className="font-semibold text-lab-primary">Problem:</span> {item.problem}</p>
                    <p className="text-lab-text-muted"><span className="font-semibold text-lab-primary">Lösung:</span> {item.solution}</p>
                    <p className="text-lab-accent font-medium"><CheckCircle className="w-4 h-4 inline mr-1" />{item.result}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Leistungen */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-playfair text-2xl lg:text-3xl font-semibold text-lab-primary mb-8">Was wir entwickeln</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              { icon: Code2, title: "Individuelle Web-Applikationen", text: "Massgeschneiderte Anwendungen für Ihre Geschäftsprozesse — von internen Tools über Kundenportale bis hin zu komplexen Plattformen. Modern, responsiv und auf Ihre Workflows zugeschnitten." },
              { icon: Wrench, title: "Erweiterung bestehender Systeme", text: "Ihre aktuelle Software kann mehr — sie braucht nur die richtigen Ergänzungen. Wir erweitern Ihre Systeme um fehlende Funktionen, Schnittstellen und Automatisierungen." },
              { icon: Plug, title: "API-Entwicklung & Integration", text: "Nahtlose Verbindung zwischen Ihren Systemen: REST APIs, Webhooks und Echtzeit-Datenströme. Schluss mit manueller Datenübertragung zwischen Programmen." },
              { icon: Shield, title: "Wartung & laufender Support", text: "Software ist nie fertig. Wir betreuen Ihre Anwendung langfristig: Sicherheitsupdates, Performance-Optimierung und neue Features, wenn Sie sie brauchen." },
            ].map((item) => (
              <motion.div key={item.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
                className="bg-lab-bg-light p-8 border border-lab-border">
                <item.icon className="w-6 h-6 text-lab-accent mb-4" strokeWidth={1.5} />
                <h3 className="font-playfair text-xl font-semibold text-lab-primary mb-3">{item.title}</h3>
                <p className="text-lab-text-muted leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vorgehen */}
      <section className="bg-lab-bg-dark py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-playfair text-2xl lg:text-3xl font-semibold text-white mb-12">Unser Entwicklungsprozess</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: "01", title: "Anforderungen", text: "Ihre Bedürfnisse verstehen, User Stories definieren und die technische Architektur gemeinsam planen." },
              { step: "02", title: "Design & Prototyp", text: "UI/UX Design und interaktiver Prototyp zur Abnahme — bevor eine Zeile Code geschrieben wird." },
              { step: "03", title: "Entwicklung", text: "Agile Sprints mit wöchentlichen Demos. Sie sehen jede Woche den Fortschritt und können steuern." },
              { step: "04", title: "Go-Live & Support", text: "Deployment, Monitoring und Übergabe — mit Dokumentation und Schulung für Ihr Team." },
            ].map((item, i) => (
              <motion.div key={item.step} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.15 }}
                className="text-white">
                <span className="text-lab-accent text-4xl font-playfair font-semibold">{item.step}</span>
                <h3 className="font-playfair text-xl font-semibold mt-4 mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-lab-primary py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-playfair text-2xl lg:text-3xl font-semibold text-white mb-4">
            Haben Sie eine Idee, die Software braucht?
          </h2>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Ob kleines internes Tool oder komplexe Geschäftsanwendung — in einem kostenlosen Erstgespräch besprechen wir Ihre Anforderungen und zeigen Ihnen, wie eine Umsetzung aussehen kann.
          </p>
          <p className="text-lab-accent font-medium mb-8">
            Inkl. grober Aufwandschätzung und Technologie-Empfehlung. Unverbindlich.
          </p>
          <button onClick={goToScheduler} className="bg-lab-accent text-lab-primary font-semibold px-10 py-4 text-base inline-flex items-center gap-2 hover:bg-white transition-colors" data-testid="cta-scheduler">
            Projekt kostenlos besprechen <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>
    </div>
  );
}
